import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Section, Container, Eyebrow, RevealHeading } from "@/components/primitives";
import { Seo } from "@/components/Seo";
import { api } from "@/lib/api";
import { INSIGHT_CATEGORIES, IMAGES } from "@/data/site";

export default function Insights() {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get("/insights", { params: cat === "All" ? {} : { category: cat } })
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [cat]);

  return (
    <>
      <Seo
        title="Insights | GovX Intelligence"
        description="Ideas, data and analysis on the institutions shaping Nigeria — policy briefs, governance explainers, data stories and GovX analysis."
      />
      <section className="relative bg-govx-black pt-[74px] text-govx-paper grain">
        <Container className="relative z-10 py-20 lg:py-24">
          <Eyebrow dark>GovX Intelligence</Eyebrow>
          <RevealHeading
            lines={["Ideas, Data & Analysis", "On The Institutions", "Shaping Nigeria."]}
            className="mt-7 font-display text-4xl font-medium leading-[1.02] tracking-tight sm:text-6xl"
          />
        </Container>
      </section>

      <Section className="py-16 lg:py-24">
        <Container>
          <div className="flex flex-wrap gap-2.5 border-b border-govx-charcoal/10 pb-8">
            {INSIGHT_CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`insight-cat-${c.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => setCat(c)}
                className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                  cat === c
                    ? "border-govx-green bg-govx-green text-white"
                    : "border-govx-charcoal/20 text-govx-charcoal/70 hover:border-govx-green hover:text-govx-green"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {loading ? (
            <p className="py-20 text-center font-mono text-sm text-govx-grey">Loading intelligence…</p>
          ) : items.length === 0 ? (
            <p className="py-20 text-center font-mono text-sm text-govx-grey">No articles in this category yet.</p>
          ) : (
            <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {items.map((it, i) => (
                <motion.div
                  key={it.slug}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.55, delay: (i % 3) * 0.08 }}
                >
                  <Link to={`/insights/${it.slug}`} data-testid={`insight-card-${i}`} className="group block">
                    <div className="relative aspect-[16/10] overflow-hidden border border-govx-charcoal/10">
                      <img
                        src={it.image_url || IMAGES.lagosColor}
                        alt={it.title}
                        className="h-full w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
                      />
                    </div>
                    <div className="mt-5 flex items-center gap-3 font-mono text-[0.62rem] uppercase tracking-[0.16em] text-govx-green">
                      <span>{it.category}</span>
                      <span className="text-govx-grey">/ {it.content_type}</span>
                    </div>
                    <h3 className="mt-3 font-display text-xl font-medium leading-snug tracking-tight transition-colors group-hover:text-govx-green">
                      {it.title}
                    </h3>
                    <p className="mt-2 line-clamp-2 text-sm text-govx-charcoal/60">{it.excerpt}</p>
                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-govx-green">
                      Read <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </Container>
      </Section>
    </>
  );
}
